import { useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'
import { openPortalPrint } from '../portal/print'

type NoticeType = 'success' | 'error' | 'warning' | 'info'

type PortalData = {
  partner: Record<string, string | null>
  invoice_window: { start_day: number; end_day: number; is_open: boolean }
  prospects: Array<Record<string, any>>
  assignments: Array<Record<string, any>>
  commissions: Array<Record<string, any>>
  invoices: Array<Record<string, any>>
  notifications: Array<Record<string, any>>
}

const sessionKey = `webapp_${activeTenant.code.toLowerCase()}_commercial_partner_session`
const endpoint = `${String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '')}/functions/v1/commercial-partner-documents`
const anonKey = String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || '')
const money = (value: unknown, currency = 'USD') =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency }).format(Number(value || 0))
const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char]!)
const statusLabel = (value: unknown) => ({ draft: 'Borrador', approved: 'Por facturar', invoiced: 'Factura enviada', payable: 'Programada', paid: 'Pagada', submitted: 'En revisión', reviewing: 'En revisión', rejected: 'Rechazada', active: 'Activa', linked: 'Vinculado' }[String(value)] || String(value || '—'))
const statusClass = (value: unknown) => ['paid', 'approved', 'linked'].includes(String(value)) ? 'success' : ['rejected', 'cancelled'].includes(String(value)) ? 'error' : ['payable', 'invoiced', 'submitted', 'reviewing'].includes(String(value)) ? 'info' : 'warning'
const formatDate = (value: unknown) => value ? new Date(String(value) + (String(value).length === 10 ? 'T12:00:00' : '')).toLocaleDateString('es-DO') : '—'

function setText(id: string, value: unknown) {
  const node = document.getElementById(id)
  if (node) node.textContent = String(value ?? '—')
}

function renderPortal(data: PortalData) {
  const approved = data.commissions.filter(item => item.status !== 'draft')
  const due = approved.reduce((sum, item) => sum + (item.status === 'paid' ? 0 : Number(item.amount || 0)), 0)
  const sold = data.assignments.filter(item => item.sale_id)
  setText('brokerPortalCode', data.partner.code)
  setText('brokerPortalName', data.partner.legal_name)
  setText('brokerKpiProspects', data.prospects.length)
  setText('brokerKpiSales', sold.length)
  setText('brokerKpiCommissions', money(due))
  setText('brokerInvoiceWindow', `${data.invoice_window.start_day}–${data.invoice_window.end_day}`)
  setText('brokerWindowCopy', `La carga se habilita del ${data.invoice_window.start_day} al ${data.invoice_window.end_day} de cada mes.`)
  const badge = document.getElementById('brokerWindowBadge')
  if (badge) {
    badge.textContent = data.invoice_window.is_open ? 'Ventana abierta' : 'Ventana cerrada'
    badge.className = `lozenge lozenge--${data.invoice_window.is_open ? 'success' : 'neutral'}`
  }
  const invoiceButton = document.getElementById('brokerInvoiceSubmit') as HTMLButtonElement | null
  if (invoiceButton) {
    invoiceButton.dataset.windowOpen = String(data.invoice_window.is_open)
    invoiceButton.disabled = !data.invoice_window.is_open
  }

  const overview = document.getElementById('brokerOverviewRows')
  if (overview) overview.innerHTML = approved.slice(0, 6).map(item => `<tr><td>${escapeHtml(item.client_name || 'Cliente asociado')}</td><td>${escapeHtml(item.sale_number || '—')}</td><td>${money(item.base_amount, item.currency)}</td><td>${money(item.amount, item.currency)}</td><td><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></td></tr>`).join('') || '<tr><td colspan="5">Aún no hay comisiones aprobadas.</td></tr>'
  const overviewCards = document.getElementById('brokerOverviewCards')
  if (overviewCards) overviewCards.innerHTML = approved.slice(0, 6).map(item => `<article class="broker-mobile-card"><div class="broker-mobile-card__top"><strong>${escapeHtml(item.client_name || 'Cliente asociado')}</strong><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><div class="broker-mobile-card__row"><span>Venta</span><strong>${escapeHtml(item.sale_number || '—')}</strong></div><div class="broker-mobile-card__row"><span>Base</span><strong>${money(item.base_amount, item.currency)}</strong></div><div class="broker-mobile-card__row"><span>Comisión</span><strong>${money(item.amount, item.currency)}</strong></div></article>`).join('') || '<p class="help broker-mobile-empty">Aún no hay comisiones aprobadas.</p>'

  const prospects = document.getElementById('brokerProspectRows')
  if (prospects) prospects.innerHTML = data.prospects.map(item => `<tr><td><strong>${escapeHtml(item.full_name)}</strong></td><td>${escapeHtml(item.phone)}</td><td>${escapeHtml(item.project_interest || '—')}</td><td>${new Date(item.created_at).toLocaleDateString('es-DO')}</td><td><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></td></tr>`).join('') || '<tr><td colspan="5">No has registrado prospectos.</td></tr>'
  const prospectCards = document.getElementById('brokerProspectCards')
  if (prospectCards) prospectCards.innerHTML = data.prospects.map(item => `<article class="broker-mobile-card"><div class="broker-mobile-card__top"><strong>${escapeHtml(item.full_name)}</strong><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><div class="broker-mobile-card__row"><span>Teléfono</span><strong>${escapeHtml(item.phone || '—')}</strong></div><div class="broker-mobile-card__row"><span>Proyecto</span><strong>${escapeHtml(item.project_interest || '—')}</strong></div><div class="broker-mobile-card__row"><span>Registrado</span><strong>${formatDate(item.created_at)}</strong></div></article>`).join('') || '<p class="help broker-mobile-empty">No has registrado prospectos.</p>'

  const sales = document.getElementById('brokerSalesRows')
  if (sales) sales.innerHTML = sold.map(item => {
    const commission = approved.find(entry => entry.sale_id === item.sale_id)
    return `<tr><td><strong>${escapeHtml(item.sale_number || '—')}</strong></td><td>${escapeHtml(item.client_name || '—')}</td><td>${money(item.sale_total)}</td><td>${escapeHtml(item.sale_date || '—')}</td><td>${commission ? money(commission.amount, commission.currency) : 'Pendiente'}</td><td><span class="lozenge lozenge--${statusClass(commission?.status)}">${escapeHtml(statusLabel(commission?.status || item.status))}</span></td></tr>`
  }).join('') || '<tr><td colspan="6">No hay ventas vinculadas a tu código.</td></tr>'
  const salesCards = document.getElementById('brokerSalesCards')
  if (salesCards) salesCards.innerHTML = sold.map(item => {
    const commission = approved.find(entry => entry.sale_id === item.sale_id)
    return `<article class="broker-mobile-card"><div class="broker-mobile-card__top"><strong>${escapeHtml(item.sale_number || 'Venta')}</strong><span class="lozenge lozenge--${statusClass(commission?.status || item.status)}">${escapeHtml(statusLabel(commission?.status || item.status))}</span></div><div class="broker-mobile-card__row"><span>Cliente</span><strong>${escapeHtml(item.client_name || '—')}</strong></div><div class="broker-mobile-card__row"><span>Precio</span><strong>${money(item.sale_total)}</strong></div><div class="broker-mobile-card__row"><span>Comisión</span><strong>${commission ? money(commission.amount, commission.currency) : 'Pendiente'}</strong></div><div class="broker-mobile-card__row"><span>Cierre</span><strong>${formatDate(item.sale_date)}</strong></div></article>`
  }).join('') || '<p class="help broker-mobile-empty">No hay ventas vinculadas a tu código.</p>'
  setText('brokerSalesVolume', money(sold.reduce((sum, item) => sum + Number(item.sale_total || 0), 0)))
  setText('brokerCommissionTotal', money(approved.reduce((sum, item) => sum + Number(item.amount || 0), 0)))
  setText('brokerCommissionPaid', money(approved.filter(item => item.status === 'paid').reduce((sum, item) => sum + Number(item.amount || 0), 0)))

  const commissionSelect = document.getElementById('brokerInvoiceCommission') as HTMLSelectElement | null
  if (commissionSelect) {
    const available = approved.filter(item => item.status === 'approved' && !item.invoice_id)
    commissionSelect.innerHTML = '<option value="">Selecciona una comisión</option>' + available.map(item => `<option value="${escapeHtml(item.id)}" data-amount="${Number(item.amount)}" data-currency="${escapeHtml(item.currency)}">${escapeHtml(item.sale_number || 'Comisión')} · ${money(item.amount, item.currency)}</option>`).join('')
  }
  const invoices = document.getElementById('brokerInvoiceRows')
  if (invoices) invoices.innerHTML = data.invoices.map(item => `<tr><td>${escapeHtml(item.invoice_number || item.id.slice(0, 8))}</td><td>${escapeHtml(item.ncf)}</td><td>${money(item.amount, item.currency)}</td><td>${new Date(item.submitted_at).toLocaleDateString('es-DO')}</td><td><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></td><td>${item.payable_status === 'paid' ? `<button class="text-link" type="button" data-payment-receipt="${escapeHtml(item.id)}" data-code="${escapeHtml(item.payable_code || item.invoice_number || item.ncf)}" data-date="${escapeHtml(item.payment_date || '')}" data-reference="${escapeHtml(item.payment_reference || '')}" data-amount="${Number(item.amount)}" data-currency="${escapeHtml(item.currency)}">Ver / descargar comprobante</button>` : escapeHtml(item.payable_code || '—')}</td></tr>`).join('') || '<tr><td colspan="6">No has enviado facturas.</td></tr>'
  const invoiceCards = document.getElementById('brokerInvoiceCards')
  if (invoiceCards) invoiceCards.innerHTML = data.invoices.map(item => `<article class="broker-mobile-card"><div class="broker-mobile-card__top"><strong>${escapeHtml(item.invoice_number || item.id.slice(0, 8))}</strong><span class="lozenge lozenge--${statusClass(item.status)}">${escapeHtml(statusLabel(item.status))}</span></div><div class="broker-mobile-card__row"><span>NCF</span><strong>${escapeHtml(item.ncf || '—')}</strong></div><div class="broker-mobile-card__row"><span>Enviada</span><strong>${formatDate(item.submitted_at)}</strong></div><div class="broker-mobile-card__row"><span>Monto</span><strong>${money(item.amount, item.currency)}</strong></div>${item.payable_status === 'paid' ? `<button class="btn btn--block" type="button" data-payment-receipt="${escapeHtml(item.id)}" data-code="${escapeHtml(item.payable_code || item.invoice_number || item.ncf)}" data-date="${escapeHtml(item.payment_date || '')}" data-reference="${escapeHtml(item.payment_reference || '')}" data-amount="${Number(item.amount)}" data-currency="${escapeHtml(item.currency)}"><i data-lucide="receipt-text"></i> Ver comprobante</button>` : `<div class="broker-mobile-card__row"><span>CxP</span><strong>${escapeHtml(item.payable_code || 'Pendiente')}</strong></div>`}</article>`).join('') || '<p class="help broker-mobile-empty">No has enviado facturas.</p>'

  const notifications = document.getElementById('brokerNotificationList')
  if (notifications) notifications.innerHTML = data.notifications.map(item => `<div class="activity"><div class="activity__icon"><i data-lucide="bell"></i></div><div><strong>${escapeHtml(item.title)}</strong><p>${escapeHtml(item.message)}</p></div></div>`).join('') || '<p class="help">No hay notificaciones nuevas.</p>'

  ;['email', 'phone', 'city', 'bank_name', 'bank_account'].forEach(key => {
    const input = document.querySelector<HTMLInputElement>(`[name="broker_profile_${key}"]`)
    if (input) input.value = String(data.partner[key] || '')
  })
  ;(window as any).lucide?.createIcons()
}

function printBrokerPortal(kind: 'sales' | 'commissions', data: PortalData) {
  const approved = data.commissions.filter(item => item.status !== 'draft')
  const sold = data.assignments.filter(item => item.sale_id)
  const partnerName = data.partner.legal_name || data.partner.name || 'Socio comercial'
  const partnerCode = data.partner.code || '—'

  if (kind === 'sales') {
    return openPortalPrint({
      eyebrow: 'Gestión comercial · Portal de socios',
      title: 'Reporte de ventas vinculadas',
      reference: `VTA-${partnerCode}`,
      meta: [
        { label: 'Vendedor / broker', value: partnerName },
        { label: 'Código comercial', value: partnerCode },
        { label: 'Ventas vinculadas', value: sold.length },
        { label: 'Volumen vendido', value: money(sold.reduce((sum, item) => sum + Number(item.sale_total || 0), 0)) },
        { label: 'Comisión calculada', value: money(approved.reduce((sum, item) => sum + Number(item.amount || 0), 0)) },
        { label: 'Comisión pagada', value: money(approved.filter(item => item.status === 'paid').reduce((sum, item) => sum + Number(item.amount || 0), 0)) },
      ],
      columns: [
        { label: 'Venta' },
        { label: 'Cliente' },
        { label: 'Fecha' },
        { label: 'Precio', align: 'right' },
        { label: 'Comisión', align: 'right' },
        { label: 'Estado', align: 'center' },
      ],
      rows: sold.map(item => {
        const commission = approved.find(entry => entry.sale_id === item.sale_id)
        return [
          item.sale_number || '—',
          item.client_name || '—',
          formatDate(item.sale_date),
          money(item.sale_total),
          commission ? money(commission.amount, commission.currency) : 'Pendiente',
          statusLabel(commission?.status || item.status),
        ]
      }),
      notes: ['Las ventas y comisiones mostradas corresponden a operaciones comerciales registradas.'],
    })
  }

  const pending = approved.filter(item => item.status !== 'paid')
  return openPortalPrint({
    eyebrow: 'Cuentas por pagar · Portal de socios',
    title: 'Estado de comisiones',
    reference: `COM-${partnerCode}`,
    meta: [
      { label: 'Beneficiario', value: partnerName },
      { label: 'Código comercial', value: partnerCode },
      { label: 'Ventana de facturación', value: `${data.invoice_window.start_day}–${data.invoice_window.end_day} de cada mes` },
      { label: 'Comisiones reconocidas', value: approved.length },
      { label: 'Facturas enviadas', value: data.invoices.length },
      { label: 'Estado de ventana', value: data.invoice_window.is_open ? 'Abierta' : 'Cerrada' },
    ],
    columns: [
      { label: 'Venta' },
      { label: 'Cliente' },
      { label: 'Base', align: 'right' },
      { label: 'Comisión', align: 'right' },
      { label: 'Factura' },
      { label: 'Estado', align: 'center' },
    ],
    rows: approved.map(item => [
      item.sale_number || '—',
      item.client_name || 'Cliente asociado',
      money(item.base_amount, item.currency),
      money(item.amount, item.currency),
      item.invoice_number || item.ncf || (item.invoice_id ? 'Enviada' : 'Pendiente'),
      statusLabel(item.status),
    ]),
    totals: [{
      label: 'Pendiente de pago',
      value: money(pending.reduce((sum, item) => sum + Number(item.amount || 0), 0)),
    }],
    notes: [
      'Las facturas quedan sujetas a validación antes de crear la cuenta por pagar.',
      'El comprobante de pago se habilita cuando la cuenta se registra como pagada.',
    ],
  })
}

function printBrokerReceipt(button: HTMLButtonElement, data: PortalData | null) {
  return openPortalPrint({
    eyebrow: 'Cuentas por pagar · Portal de socios',
    title: 'Comprobante de pago de comisión',
    reference: button.dataset.code || 'PAGO-COMISIÓN',
    autoPrint: false,
    meta: [
      { label: 'Beneficiario', value: data?.partner.legal_name || data?.partner.name || 'Socio comercial' },
      { label: 'Código comercial', value: data?.partner.code || '—' },
      { label: 'Fecha de pago', value: formatDate(button.dataset.date) },
      { label: 'Referencia bancaria', value: button.dataset.reference || '—' },
      { label: 'Moneda', value: button.dataset.currency || 'USD' },
      { label: 'Estado', value: 'Pagada' },
    ],
    columns: [
      { label: 'Concepto' },
      { label: 'Documento' },
      { label: 'Monto pagado', align: 'right' },
    ],
    rows: [[
      'Pago de comisión comercial',
      button.dataset.code || '—',
      money(button.dataset.amount, button.dataset.currency),
    ]],
    totals: [{ label: 'Total pagado', value: money(button.dataset.amount, button.dataset.currency) }],
    notes: ['Comprobante emitido al completar el pago de la cuenta por pagar.'],
    signatures: [`Preparado por ${activeTenant.tradeName}`, 'Beneficiario'],
  })
}

export function BrokerPortalBridge({ ready }: { ready: boolean }) {
  useEffect(() => {
    if (!ready || !supabase) return
    const client = supabase
    let currentData: PortalData | null = null
    const cleanups: Array<() => void> = []
    const bind = (id: string, handler: (event: SubmitEvent) => void) => {
      const form = document.getElementById(id)
      if (!form) return
      const listener = (event: Event) => handler(event as SubmitEvent)
      form.addEventListener('submit', listener, true)
      cleanups.push(() => form.removeEventListener('submit', listener, true))
    }
    const open = (view: string) => (window as any).openView?.(view)
    const token = () => sessionStorage.getItem(sessionKey) || ''
    const notify = (type: NoticeType, title: string, message: string) => {
      const region = document.getElementById('brokerNotificationRegion')
      if (!region) {
        ;(window as any).showToast?.(message)
        return
      }
      const icons: Record<NoticeType, string> = {
        success: 'circle-check',
        error: 'circle-x',
        warning: 'triangle-alert',
        info: 'info',
      }
      const item = document.createElement('div')
      item.className = `portal-notification portal-notification--${type}`
      item.setAttribute('role', type === 'error' ? 'alert' : 'status')
      item.innerHTML = `<span class="portal-notification__icon"><i data-lucide="${icons[type]}"></i></span><div class="portal-notification__copy"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(message)}</span></div><button class="portal-notification__close" type="button" aria-label="Cerrar notificación"><i data-lucide="x"></i></button>`
      const dismiss = () => {
        item.classList.add('is-leaving')
        window.setTimeout(() => item.remove(), 180)
      }
      item.querySelector('button')?.addEventListener('click', dismiss, { once: true })
      region.prepend(item)
      while (region.children.length > 3) region.lastElementChild?.remove()
      ;(window as any).lucide?.createIcons()
      window.setTimeout(dismiss, type === 'error' ? 7000 : 5000)
    }
    const setBusy = (form: HTMLFormElement, busy: boolean, idleLabel: string, busyLabel: string) => {
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')
      if (!button) return
      const invoiceWindowClosed = form.id === 'brokerInvoiceForm' && button.dataset.windowOpen === 'false'
      button.disabled = busy || invoiceWindowClosed
      const label = button.querySelector('span')
      if (label) label.textContent = busy ? busyLabel : idleLabel
      else button.textContent = busy ? busyLabel : idleLabel
      button.setAttribute('aria-busy', String(busy))
    }
    const refresh = async () => {
      const current = token()
      if (!current) return false
      const { data, error } = await client.rpc('get_commercial_partner_portal', { p_token: current })
      if (error) {
        sessionStorage.removeItem(sessionKey)
        notify('error', 'La sesión terminó', error.message || 'Vuelve a ingresar con tu código y PIN.')
        open('broker-login')
        return false
      }
      currentData = data as PortalData
      renderPortal(currentData)
      return true
    }

    bind('brokerLoginForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      setBusy(form, true, 'Entrar al portal', 'Validando…')
      const fields = new FormData(form)
      void (async () => {
        try {
          const { data, error } = await client.rpc('login_commercial_partner', {
            p_partner_code: fields.get('partner_code'),
            p_pin: fields.get('pin'),
          })
          if (error || !data?.token) throw new Error(error?.message || 'Código o PIN incorrectos.')
          sessionStorage.setItem(sessionKey, data.token)
          const loaded = await refresh()
          if (!loaded) return
          open('broker-portal')
          notify('success', 'Sesión iniciada', `Bienvenido, ${data.legal_name}.`)
        } catch (error: unknown) {
          notify('error', 'No pudimos iniciar sesión', error instanceof Error ? error.message : 'Revisa tu código y PIN e inténtalo nuevamente.')
        } finally {
          setBusy(form, false, 'Entrar al portal', 'Validando…')
        }
      })()
    })

    bind('brokerForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      setBusy(form, true, 'Enviar solicitud', 'Enviando…')
      const fields = new FormData(form)
      const policyVersion = String(fields.get('policy_version') || '').trim()
      if (!policyVersion) {
        notify('error', 'Política no disponible', 'La empresa todavía no ha publicado una política de registro vigente.')
        setBusy(form, false, 'Enviar solicitud', 'Enviando…')
        return
      }
      void (async () => {
        try {
          const { data, error } = await client.rpc('request_commercial_partner_registration', {
            p_company_id: activeTenant.companyId,
            p_partner_type: String(fields.get('partner_type') || ''),
            p_legal_name: String(fields.get('legal_name') || ''),
            p_tax_id: String(fields.get('tax_id') || ''),
            p_email: String(fields.get('email') || ''),
            p_phone: String(fields.get('phone') || ''),
            p_policy_version: policyVersion,
            p_city: String(fields.get('city') || '') || null,
            p_bank_name: String(fields.get('bank_name') || '') || null,
            p_bank_account: String(fields.get('bank_account') || '') || null,
            p_identity_document_path: null,
          })
          if (error || !data?.ok) throw new Error(error?.message || data?.message || 'No fue posible procesar la solicitud.')
          form.reset()
          open('broker-login')
          notify('success', 'Solicitud enviada', `La recibimos correctamente. ${activeTenant.tradeName} revisará tus datos y habilitará tu acceso.`)
        } catch (error: unknown) {
          notify('error', 'No pudimos enviar la solicitud', error instanceof Error ? error.message : 'Inténtalo nuevamente en unos minutos.')
        } finally {
          setBusy(form, false, 'Enviar solicitud', 'Enviando…')
        }
      })()
    })

    bind('prospectForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      setBusy(form, true, 'Guardar prospecto', 'Guardando…')
      const fields = new FormData(form)
      void (async () => {
        try {
          const { error } = await client.rpc('submit_commercial_partner_prospect', {
            p_token: token(), p_full_name: fields.get('full_name'), p_email: fields.get('email'),
            p_phone: fields.get('phone'), p_project_interest: fields.get('project_interest'), p_notes: fields.get('notes'),
          })
          if (error) throw error
          form.reset()
          notify('success', 'Prospecto registrado', 'Quedó registrado y vinculado a tu código comercial.')
          await refresh()
        } catch (error: unknown) {
          notify('error', 'No pudimos registrar el prospecto', error instanceof Error ? error.message : 'Revisa los datos e inténtalo nuevamente.')
        } finally {
          setBusy(form, false, 'Guardar prospecto', 'Guardando…')
        }
      })()
    })

    bind('brokerInvoiceForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      setBusy(form, true, 'Enviar factura', 'Enviando…')
      const body = new FormData(form)
      body.set('action', 'invoice')
      body.set('token', token())
      void (async () => {
        try {
          const response = await fetch(endpoint, { method: 'POST', headers: { apikey: anonKey }, body })
          const result = await response.json()
          if (!response.ok || !result.ok) throw new Error(result.message || 'No fue posible cargar la factura.')
          form.reset()
          notify('success', 'Factura recibida', 'Quedó registrado y pendiente de validación.')
          await refresh()
        } catch (error: unknown) {
          notify('error', 'No pudimos cargar la factura', error instanceof Error ? error.message : 'Verifica el archivo y vuelve a intentarlo.')
        } finally {
          setBusy(form, false, 'Enviar factura', 'Enviando…')
        }
      })()
    })

    bind('brokerProfileForm', event => {
      event.preventDefault(); event.stopImmediatePropagation()
      const form = event.currentTarget as HTMLFormElement
      if (!form.reportValidity()) return
      setBusy(form, true, 'Guardar cambios', 'Guardando…')
      const fields = new FormData(form)
      void (async () => {
        try {
          const { error } = await client.rpc('update_commercial_partner_profile', {
            p_token: token(), p_email: fields.get('broker_profile_email'), p_phone: fields.get('broker_profile_phone'),
            p_city: fields.get('broker_profile_city'), p_bank_name: fields.get('broker_profile_bank_name'),
            p_bank_account: fields.get('broker_profile_bank_account'),
          })
          if (error) throw error
          notify('success', 'Datos actualizados', 'Los cambios quedaron guardados en tu cuenta comercial.')
          await refresh()
        } catch (error: unknown) {
          notify('error', 'No pudimos guardar los cambios', error instanceof Error ? error.message : 'Inténtalo nuevamente.')
        } finally {
          setBusy(form, false, 'Guardar cambios', 'Guardando…')
        }
      })()
    })

    const logoutButton = document.getElementById('brokerLogoutButton')
    const logout = () => {
      sessionStorage.removeItem(sessionKey)
      open('broker-login')
      notify('info', 'Sesión cerrada', 'Saliste del portal comercial correctamente.')
    }
    logoutButton?.addEventListener('click', logout)
    cleanups.push(() => logoutButton?.removeEventListener('click', logout))

    const select = document.getElementById('brokerInvoiceCommission')
    const change = () => {
      const option = (select as HTMLSelectElement)?.selectedOptions[0]
      const amount = document.getElementById('brokerInvoiceAmount') as HTMLInputElement | null
      const currency = document.getElementById('brokerInvoiceCurrency') as HTMLSelectElement | null
      if (amount) amount.value = option?.dataset.amount || ''
      if (currency && option?.dataset.currency) currency.value = option.dataset.currency
    }
    select?.addEventListener('change', change)
    cleanups.push(() => select?.removeEventListener('change', change))

    const receiptClick = (event: Event) => {
      const target = event.target as HTMLElement
      const printButton = target.closest<HTMLButtonElement>('[data-broker-print]')
      if (printButton) {
        if (!currentData) {
          notify('warning', 'Información no disponible', 'Espera a que termine de cargar el portal.')
          return
        }
        const opened = printBrokerPortal(printButton.dataset.brokerPrint as 'sales' | 'commissions', currentData)
        if (!opened) notify('warning', 'Ventana bloqueada', 'Permite ventanas emergentes para abrir el documento.')
        return
      }

      const button = target.closest<HTMLButtonElement>('[data-payment-receipt]')
      if (!button) return
      const opened = printBrokerReceipt(button, currentData)
      if (!opened) notify('warning', 'Ventana bloqueada', 'Permite ventanas emergentes para abrir el comprobante.')
    }
    document.addEventListener('click', receiptClick)
    cleanups.push(() => document.removeEventListener('click', receiptClick))

    if (token()) void refresh()
    return () => cleanups.forEach(cleanup => cleanup())
  }, [ready])
  return null
}
